/**
 * HCOL (High Cost of Living) ZIP codes — California prime metros, v1.
 *
 * Used by leasedFeeValuation.ts to apply a -25 bp adjustment to the cap
 * rate when a subject property sits in a defined HCOL zip. v1 is a static
 * list curated from the CA metros that consistently price tighter than
 * the state average (median home value > ~$1.5M, or top 5% by income).
 *
 * Future v2: replace with a programmatic check against demographic data
 * (median home value or median income) returned by TAM / census on the
 * station record.
 *
 * Categories (all CA):
 *   - SF / Peninsula / Silicon Valley
 *   - Marin
 *   - East Bay premier (Piedmont, Berkeley Hills, Lamorinda)
 *   - LA Westside / coastal South Bay
 *   - Coastal Orange County
 *   - San Diego coastal (La Jolla, Del Mar, Rancho Santa Fe, Coronado)
 *   - Santa Barbara / Montecito coastal
 *   - Tahoe lakefront (selected)
 */

const ZIPS: ReadonlyArray<string> = [
  // === San Francisco proper ===
  '94102', '94103', '94104', '94105', '94107', '94108', '94109', '94110',
  '94111', '94112', '94114', '94115', '94116', '94117', '94118', '94121',
  '94122', '94123', '94124', '94127', '94129', '94130', '94131', '94132',
  '94133', '94134', '94158',

  // === Peninsula ===
  // Daly City / Brisbane / South SF (suburban-but-tight)
  '94014', '94015', '94005', '94080',
  // San Mateo, Burlingame, Hillsborough, Foster City
  '94401', '94402', '94403', '94404', '94010', '94011',
  // Atherton, Menlo Park, Portola Valley, Woodside
  '94025', '94027', '94028', '94062',
  // Palo Alto, East Palo Alto, Stanford, Los Altos, Los Altos Hills
  '94301', '94302', '94303', '94304', '94305', '94306',
  '94022', '94023', '94024',
  // Redwood City, Belmont, San Carlos
  '94061', '94062', '94063', '94064', '94065', '94002', '94070',
  // Half Moon Bay coast
  '94019', '94037',

  // === Silicon Valley ===
  // Mountain View, Sunnyvale, Santa Clara
  '94035', '94039', '94040', '94041', '94043',
  '94085', '94086', '94087', '94089',
  '95050', '95051', '95054',
  // Cupertino, Saratoga, Los Gatos, Monte Sereno
  '95014', '95015', '95070', '95030', '95032', '95033',
  // Campbell, Willow Glen, Rose Garden (San Jose desirable)
  '95008', '95125', '95126', '95128', '95129', '95130', '95124',

  // === Marin ===
  // Sausalito, Tiburon, Belvedere, Mill Valley, Corte Madera, Larkspur
  '94965', '94920', '94941', '94925', '94939',
  // Ross, Kentfield, Greenbrae, San Anselmo, Fairfax
  '94957', '94904', '94960', '94930',
  // San Rafael (premium areas), Novato (limited)
  '94901', '94903', '94945',

  // === East Bay premier ===
  // Piedmont, Berkeley Hills, Albany, Kensington
  '94611', '94618', '94705', '94707', '94708', '94709', '94710',
  // Lafayette, Orinda, Moraga (Lamorinda)
  '94549', '94563', '94556',
  // Walnut Creek, Alamo, Danville, Diablo, Blackhawk
  '94595', '94596', '94507', '94506', '94526', '94528',
  // Oakland Hills (montclair/rockridge), Emeryville
  '94602', '94619', '94705', '94608',

  // === Santa Cruz / Monterey / Carmel coastal ===
  '95060', '95062', '95065', '95003', '95010',
  '93923', '93921', '93953', '93940', '93942',

  // === Santa Barbara / Montecito ===
  '93108', '93109', '93101', '93103', '93105', '93110',
  '93013', '93067',  // Carpinteria / Summerland

  // === LA — Westside / Beaches ===
  // Beverly Hills, Bel Air, Holmby Hills, Westwood
  '90210', '90211', '90212', '90077', '90024',
  // Brentwood, Pacific Palisades, Mandeville Canyon
  '90049', '90272',
  // Santa Monica, Venice
  '90401', '90402', '90403', '90404', '90405', '90291', '90292',
  // Malibu
  '90263', '90264', '90265',
  // Manhattan Beach, Hermosa Beach, Redondo Beach (premium)
  '90266', '90254', '90277', '90278',
  // Palos Verdes (RPV, Rolling Hills, PV Estates)
  '90274', '90275',
  // Marina del Rey, Playa del Rey, El Segundo
  '90292', '90293', '90245',
  // West Hollywood, Hancock Park, Hollywood Hills
  '90069', '90048', '90046', '90068', '90004',

  // === LA — premium hills/valleys ===
  // Studio City, Sherman Oaks (south of Ventura), Encino, Tarzana, Calabasas, Hidden Hills
  '91604', '91423', '91403', '91436', '91356', '91302', '91301',
  // Pasadena (San Marino, Sierra Madre, La Cañada Flintridge premium)
  '91108', '91024', '91011', '91030',
  // Toluca Lake, Burbank Hills (limited)
  '91602',

  // === Coastal Orange County ===
  // Newport Beach, Corona del Mar, Newport Coast
  '92660', '92661', '92662', '92663', '92625', '92657',
  // Laguna Beach, Laguna Niguel coastal
  '92651', '92677',
  // Dana Point, San Clemente coastal
  '92624', '92629', '92672', '92673',
  // Huntington Beach (premium coastal), Seal Beach
  '92648', '92649', '90740', '90742', '90743',
  // Coto de Caza, Ladera Ranch, Trabuco premium
  '92679',

  // === San Diego coastal ===
  // La Jolla
  '92037', '92038', '92039', '92093',
  // Del Mar, Solana Beach, Cardiff, Encinitas
  '92014', '92075', '92007', '92024',
  // Rancho Santa Fe, Fairbanks Ranch
  '92067', '92091',
  // Coronado, Point Loma, Sunset Cliffs
  '92118', '92106', '92107',
  // Carmel Valley, Torrey Pines (San Diego)
  '92130',

  // === Tahoe lakefront (selected, premium only) ===
  '96150', '96145', '96143', '96146', '96148',
];

export const HCOL_ZIPS: ReadonlySet<string> = new Set(ZIPS);
